import { SearchRequest } from './dfv-search-request.js';
import { deviceCollections } from './dfv-model-collection-index.js';
import { brandKeys, chooseModels, normalized } from './dfv-model-resolver-v13.js?v=hf17-cro-20261016-1';

function dfvNorm(s){return String(s||'').normalize('NFKC').toLowerCase().replace(/\+/g,' plus ').replace(/[^\p{L}\p{N}]+/gu,' ').replace(/\s+/g,' ').trim().replace(/^samsung /,'');}
function dfvModels(query,sources=[]){
 const q=dfvNorm(query);if(q.length<2)return [];
 const all=new Map();
 deviceCollections.forEach(x=>all.set(x.handle,{name:x.name,url:x.handle}));
 sources.forEach(s=>{const handle=s.url?.split('/collections/')[1]?.split(/[?#]/)[0];if(handle&&!all.has(handle))all.set(handle,{name:s.name,url:handle});});
 const words=q.split(' ').filter(Boolean);
 return [...all.values()].filter(item=>words.every(w=>dfvNorm(item.name).includes(w))).sort((a,b)=>a.name.length-b.name.length||a.name.localeCompare(b.name)).slice(0,10);
}

class DFVDeviceSearch extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.results = this.querySelector('[data-dfv-results]');
    this.status = this.querySelector('[data-dfv-status]');
    if (!this.input || !this.form || !this.results || !this.status) return;
    this.events = new AbortController();
    this.indexUrls = JSON.parse(this.querySelector('[data-dfv-index-urls]')?.textContent || '{}');
    this.indexRequests = new Map();
    this.modelEpoch = 0;
    this.lastModelQuery = '';
    this.lastModelChoices = [];

    this.request = new SearchRequest(new URL(this.dataset.endpoint, location.origin).href, {
      fetch: window.fetch.bind(window),
      success: (html, query) => this.showResults(html, query),
      error: () => {
        this.close();
        this.status.textContent = this.dataset.error;
      },
    });
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-controls', this.results.id);
    this.input.setAttribute('aria-expanded', 'false');
    this.results.setAttribute('role', 'listbox');
    this.results.setAttribute('aria-labelledby', this.input.id + '-label');
    const options = { signal: this.events.signal };
    this.input.addEventListener('input', event => {
      if (!event.isComposing) this.search();
    }, options);
    this.input.addEventListener('compositionstart', () => this.dismiss(), options);
    this.input.addEventListener('compositionend', () => this.search(), options);
    this.input.addEventListener('keydown', event => this.onKeydown(event), options);
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2) this.search();
    }, options);
    this.addEventListener('focusout', event => {
      if (this.pointerSelecting && !event.relatedTarget) return;
      if (!this.contains(event.relatedTarget)) this.dismiss();
    }, options);
    this.results.addEventListener('pointerdown', event => {
      if (event.target.closest('[data-dfv-option]')) this.pointerSelecting = true;
    }, options);
    document.addEventListener('pointerup', () => {
      setTimeout(() => { this.pointerSelecting = false; }, 0);
    }, options);
    document.addEventListener('pointercancel', () => {
      this.pointerSelecting = false;
      if (!this.contains(document.activeElement)) this.dismiss();
    }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.dismiss();
    }, options);
    this.form.addEventListener('submit', event => {
      const branded=brandKeys(this.input.value);
      if(branded.length){
        event.preventDefault();
        const query=this.input.value.trim();
        const matches=this.lastModelQuery===query?this.lastModelChoices:[];
        if(matches.length===1&&normalized(query)===normalized(matches[0].name)){
          location.assign(this.collectionUrl(matches[0].handle));
          return;
        }
        // Ambiguous or unknown device. Require exact model selection; never silently mix variants.
        this.status.textContent=this.dataset.modelGroup;
        this.search();
        return;
      }
      const choices=dfvModels(this.input.value);
      if(choices.length>1 && this.selected<0){
        event.preventDefault();
        this.status.textContent=this.dataset.modelGroup;
        this.search();
        return;
      }
      if(choices.length===1 && dfvNorm(choices[0].name)===dfvNorm(this.input.value)){
        event.preventDefault();
        const root=this.dataset.root||'/';
        location.assign(this.collectionUrl(choices[0].url));
        return;
      }

      this.input.value = this.input.value.trim();
      if (!this.input.value) {
        event.preventDefault();
        this.input.focus();
      }
      this.dismiss();
    }, options);
  }

  disconnectedCallback() {
    this.dismiss();
    this.events?.abort();
    this.events = null;
  }

  search() {
    this.dismiss();
    const query = this.input.value.trim();
    if (query.length < 2) return;
    this.status.textContent = this.dataset.loading;
    const keys=brandKeys(query);
    if(keys.length){
      const epoch=this.modelEpoch;
      this.timer=setTimeout(async()=>{
        try {
          const batches=await Promise.all(keys.map(key=>this.loadIndex(key)));
          if(epoch!==this.modelEpoch || this.input.value.trim()!==query || !this.contains(document.activeElement))return;
          this.showIndexedModels(query,batches.flat());
        } catch(error) {
          if(epoch!==this.modelEpoch)return;
          this.close();
          this.status.textContent=this.dataset.error;
        }
      },190);
      return;
    }
    this.timer = setTimeout(() => this.request.search(query), 220);
  }

  dismiss() {
    this.modelEpoch=(this.modelEpoch||0)+1;
    this.lastModelChoices=[];
    this.lastModelQuery='';
    this.pointerSelecting = false;
    clearTimeout(this.timer);
    this.request?.cancel();
    this.close();
    if (this.status) this.status.textContent = '';
  }

  close() {
    if (!this.results || !this.input) return;
    this.results.hidden = true;
    this.results.replaceChildren();
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.options = [];
    this.selected = -1;
  }


  collectionUrl(handle) {
    const root=this.dataset.root||'/';
    const url = new URL((root.endsWith('/')?root:root+'/')+'collections/'+encodeURIComponent(handle),location.origin).href;
    return window.DFVCro ? window.DFVCro.withTypeUrl(url, window.DFVCro.currentType(), location.origin) : url;
  }

  loadIndex(key) {
    const url=this.indexUrls[key];
    if(!url)return Promise.resolve([]);
    if(!this.indexRequests.has(key)){
      const promise=fetch(url).then(r=>{if(!r.ok)throw new Error('Index '+key+' '+r.status);return r.json()})
       .then(data=>Array.isArray(data.devices)?data.devices:[])
       .catch(e=>{this.indexRequests.delete(key);throw e});
      this.indexRequests.set(key,promise);
    }
    return this.indexRequests.get(key);
  }

  showIndexedModels(query,records) {
    if(this.input.value.trim()!==query || !this.contains(document.activeElement))return;
    this.close();
    const models=chooseModels(query,records,9);
    this.lastModelQuery=query;
    this.lastModelChoices=models;
    if(!models.length){
      this.status.textContent=this.dataset.empty;
      const help=document.createElement('p');
      help.className='dfv-finder__model-warning';
      help.textContent=this.dataset.empty;
      this.results.append(help);
      if(this.dataset.brandsUrl){
        const link=document.createElement('a');link.className='dfv-finder__model-warning';
        const type=window.DFVCro?.currentType();
        link.href=this.dataset.brandsUrl+(type?'#dfv-type='+encodeURIComponent(type):'');
        link.textContent=this.dataset.brandsLabel;this.results.append(link);
      }
      this.results.hidden=false;
      return;
    }
    const heading=document.createElement('p');
    heading.className='dfv-finder__group-heading';heading.textContent=this.dataset.modelGroup;
    this.results.append(heading);
    const ul=document.createElement('ul');ul.className='dfv-finder__suggestions';ul.setAttribute('role','presentation');
    models.forEach(item=>{
      const li=document.createElement('li');li.setAttribute('role','presentation');
      const link=document.createElement('a');
      link.className='dfv-finder__suggestion dfv-finder__suggestion--model';
      link.setAttribute('data-dfv-option','');
      link.setAttribute('aria-label',this.dataset.modelLabel+': '+item.name+(item.ambiguous?' — '+item.handle:''));
      link.href=this.collectionUrl(item.handle);
      const icon=document.createElement('span');icon.className='dfv-finder__model-icon';icon.setAttribute('aria-hidden','true');icon.textContent='▯';
      const name=document.createElement('span');name.textContent=item.name;
      const copy=document.createElement('span');copy.className='dfv-finder__suggestion-copy';copy.append(name);
      if(item.ambiguous){
        const ref=document.createElement('small');ref.className='dfv-finder__collection-ref';ref.textContent=item.handle;
        copy.append(ref);
      }
      const arrow=document.createElement('span');arrow.className='dfv-finder__model-chevron';arrow.setAttribute('aria-hidden','true');arrow.textContent='›';
      link.append(icon,copy,arrow);li.append(link);ul.append(li);
    });
    this.results.append(ul);
    // HF14 P1: An undated model and a year-labelled collection may coexist.
    // Keep both original destinations: a similar name NEVER proves hardware
    // equivalence or that their accessory inventory can be safely merged.
    // Offer a separate product-title search spanning both, without asserting fit.
    const noYear = name => normalized(name).replace(/\s+(?:19|20)\d{2}$/, '');
    const hasYear = name => /\s+(?:19|20)\d{2}$/.test(normalized(name));
    const sameBasePair = models.find((model, i) =>
      models.some((other, j) => i !== j &&
        noYear(model.name) === noYear(other.name) &&
        normalized(model.name) !== normalized(other.name) &&
        hasYear(model.name) !== hasYear(other.name)
      )
    );
    if (sameBasePair && !(window.DFVCro && window.DFVCro.currentType())) {
      const baseName = models.find(m => normalized(m.name) === noYear(sameBasePair.name))?.name || sameBasePair.name.replace(/\s*\(?(?:19|20)\d{2}\)?\s*$/, '');
      const root = this.dataset.root || '/';
      const url = new URL((root.endsWith('/') ? root : root + '/') + 'search', location.origin);
      url.searchParams.set('q', baseName);
      url.searchParams.set('type', 'product');
      const locale = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
      const labels = {
        en: 'Search product titles mentioning', es: 'Buscar productos que mencionan',
        de: 'Produkte nach Modellbezeichnung suchen', fr: 'Rechercher les produits mentionnant',
        it: 'Cerca prodotti che riportano', ja: 'この機種名を含む商品を検索'
      };
      const broad = document.createElement('a');
      broad.className = 'dfv-finder__suggestion dfv-finder__suggestion--model';
      broad.setAttribute('data-dfv-option', '');
      broad.href = url.href;
      broad.textContent = `${labels[locale] || labels.en}: ${baseName}`;
      this.results.append(broad);
    }
    const note=document.createElement('p');
    note.className='dfv-finder__model-warning';
    note.textContent=this.dataset.searchFallback;
    this.results.append(note);
    this.options=Array.from(this.results.querySelectorAll('[data-dfv-option]'));
    this.options.forEach((option,index)=>{
      option.id=this.input.id+'-option-'+index;option.setAttribute('role','option');
      option.setAttribute('aria-selected','false');option.tabIndex=-1;
    });
    this.selected=-1;
    this.results.hidden=false;
    this.input.setAttribute('aria-expanded','true');
    this.status.textContent=this.dataset.count;
  }

  showResults(html, query) {
    if (this.input.value.trim() !== query || !this.contains(document.activeElement)) return;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const suggestions = documentFragment.querySelector('[data-dfv-suggestions]');
    if (!suggestions) throw new Error('Search section missing');
    this.close();


    const collections=Array.from(suggestions.querySelectorAll('[data-dfv-collection-url]'),el=>({name:el.getAttribute('data-dfv-model-source'),url:el.getAttribute('data-dfv-collection-url')}));
    const models=dfvModels(query,collections);
    const group=(title)=>{const p=document.createElement('p');p.className='dfv-finder__group-heading';p.textContent=title;this.results.append(p);};
    if(models.length){
      group(this.dataset.modelGroup);
      const ul=document.createElement('ul');ul.className='dfv-finder__suggestions';ul.setAttribute('role','presentation');
      models.forEach(item=>{
        const li=document.createElement('li');li.setAttribute('role','presentation');
        const link=document.createElement('a');link.className='dfv-finder__suggestion dfv-finder__suggestion--model';link.setAttribute('data-dfv-option','');
        link.setAttribute('aria-label',this.dataset.modelLabel+': '+item.name);
        const root=this.dataset.root||'/';
        link.href=this.collectionUrl(item.url);
        const icon=document.createElement('span');icon.className='dfv-finder__model-icon';icon.setAttribute('aria-hidden','true');icon.textContent='▯';
        const name=document.createElement('span');name.textContent=item.name;
        const arrow=document.createElement('span');arrow.className='dfv-finder__model-chevron';arrow.setAttribute('aria-hidden','true');arrow.textContent='›';
        link.append(icon,name,arrow);li.append(link);ul.append(li);
      });
      this.results.append(ul);
      const note=document.createElement('p');note.className='dfv-finder__model-warning';note.textContent=this.dataset.searchFallback;this.results.append(note);
    }
    // Device-only autocomplete: never substitute unrelated product suggestions.
    this.options = Array.from(this.results.querySelectorAll('[data-dfv-option]'));
    if (!this.options.length) {
      this.status.textContent = this.dataset.empty;
      return;
    }
    this.options.forEach((option, index) => {
      option.id = this.input.id + '-option-' + index;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.tabIndex = -1;
    });
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.status.textContent = this.dataset.count;
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      this.dismiss();
      return;
    }
    if (!this.options?.length || this.results.hidden) return;
    if (event.key === 'Enter' && this.selected >= 0) {
      event.preventDefault();
      this.options[this.selected].click();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    this.selected = this.selected < 0
      ? (step > 0 ? 0 : this.options.length - 1)
      : (this.selected + step + this.options.length) % this.options.length;
    this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.selected)));
    const selected = this.options[this.selected];
    this.input.setAttribute('aria-activedescendant', selected.id);
    selected.scrollIntoView({ block: 'nearest' });
  }
}

if (!customElements.get('dfv-device-search')) customElements.define('dfv-device-search', DFVDeviceSearch);
